import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import { Modal, MultiSelect } from '../components/adminModals';
import Drawer from '../../sharedComponents/Drawer';
import {
    journalAssessmentReportService,
    journalsByDeskService,
    submissionsByDeskService,
    JournalAssessmentProcessItem,
    JournalAssessmentAssignmentSummary,
    JournalAssessmentFinalDecisionDetail,
    JournalAccessorDropdownItem,
    DeskItem,
} from '../services/adminAuth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/hooks/auth';
import { Eye, CheckCircle, Clock, XCircle, AlertTriangle } from 'lucide-react';
import AdminSubmissionDetails from '../components/AdminSubmissionDetails';

function JournalAssessmentReports() {
    const { showNotification } = useNotification();
    const { user } = useAuth();
    const navigate = useNavigate();

    const isJournalConvener = user?.role === 'journalconvener';

    // ── Table state ───────────────────────────────────────────────────────────
    const [items, setItems] = useState<JournalAssessmentProcessItem[]>([]);
    const [loading, setLoading] = useState(true);

    // ── Pagination ────────────────────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // ── Filters ───────────────────────────────────────────────────────────────
    const [search, setSearch] = useState('');
    const [completedFilter, setCompletedFilter] = useState<boolean>(isJournalConvener ? false : true);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // ── Final Decision Modal ──────────────────────────────────────────────────
    const [isFinalDecisionOpen, setIsFinalDecisionOpen] = useState(false);
    const [finalDecisionTarget, setFinalDecisionTarget] = useState<JournalAssessmentProcessItem | null>(null);
    const [fdDecision, setFdDecision] = useState<'' | '3' | '4'>('');
    const [fdComment, setFdComment] = useState('');
    const [fdToWorkflowStage, setFdToWorkflowStage] = useState<number | ''>('');
    const [deskList, setDeskList] = useState<DeskItem[]>([]);
    const [deskListLoading, setDeskListLoading] = useState(false);
    const [isSubmittingFinalDecision, setIsSubmittingFinalDecision] = useState(false);

    // ── View Final Decision Drawer ────────────────────────────────────────────
    const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false);
    const [viewDecisionData, setViewDecisionData] = useState<JournalAssessmentFinalDecisionDetail | null>(null);
    const [loadingDecision, setLoadingDecision] = useState(false);

    // ── Add Panel Member Modal ────────────────────────────────────────────────
    const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
    const [addPanelTarget, setAddPanelTarget] = useState<JournalAssessmentProcessItem | null>(null);
    const [addPanelMembers, setAddPanelMembers] = useState<JournalAccessorDropdownItem[]>([]);
    const [fetchingAddMembers, setFetchingAddMembers] = useState(false);
    const [selectedAddMemberIds, setSelectedAddMemberIds] = useState<(string | number)[]>([]);
    const [isAddingPanel, setIsAddingPanel] = useState(false);

    // ── Delete Panel Member Modal ─────────────────────────────────────────────
    const [isDeletePanelOpen, setIsDeletePanelOpen] = useState(false);
    const [deletePanelTarget, setDeletePanelTarget] = useState<JournalAssessmentProcessItem | null>(null);
    const [selectedDeleteAssignmentId, setSelectedDeleteAssignmentId] = useState('');
    const [isDeletingMember, setIsDeletingMember] = useState(false);

    // ── Submission Details Drawer ─────────────────────────────────────────────
    const [isSubmissionDetailsOpen, setIsSubmissionDetailsOpen] = useState(false);
    const [detailsSubmissionId, setDetailsSubmissionId] = useState<string | null>(null);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        const handler = setTimeout(() => { fetchItems(); }, 400);
        return () => clearTimeout(handler);
    }, [currentPage, pageSize, search, completedFilter]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await journalAssessmentReportService.getJournalsByAssessmentStatus({
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
                search,
                completed: completedFilter,
            });
            if (response.isSuccess && response.data) {
                setItems(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch journal assessments' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching journal assessments' });
        } finally {
            setLoading(false);
        }
    };

    // ── Final Decision Modal ──────────────────────────────────────────────────
    const openFinalDecisionModal = async (row: JournalAssessmentProcessItem) => {
        const assignments = row.assignments ?? [];
        // Strict 3-member panel rule (kept for reference, temporarily relaxed — at least 1 required):
        // if (assignments.length < 3) {
        //     showNotification('error', {
        //         title: 'Insufficient Panel Members',
        //         message: 'A minimum of 3 panel members is required to make a final decision.',
        //     });
        //     return;
        // }
        if (assignments.length < 1) {
            showNotification('error', {
                title: 'No Panel Members Assigned',
                message: 'At least 1 panel member must be assigned to make a final decision.',
            });
            return;
        }
        const hasIncomplete = assignments.some(a => a.status?.toLowerCase() !== 'completed');
        if (hasIncomplete) {
            showNotification('error', {
                title: 'Cannot Make Final Decision',
                message: 'All panel assessments must be completed before making a final decision.',
            });
            return;
        }
        setFinalDecisionTarget(row);
        setFdDecision('');
        setFdComment('');
        setFdToWorkflowStage('');
        setIsFinalDecisionOpen(true);
        setDeskListLoading(true);
        try {
            const res = await journalsByDeskService.getJournalDeskList();
            setDeskList(res.isSuccess && res.data ? res.data : []);
        } catch {
            setDeskList([]);
            showNotification('error', { title: 'Error', message: 'Failed to load desk list' });
        } finally {
            setDeskListLoading(false);
        }
    };

    const closeFinalDecisionModal = () => {
        if (isSubmittingFinalDecision) return;
        setIsFinalDecisionOpen(false);
        setFinalDecisionTarget(null);
        setFdDecision('');
        setFdComment('');
        setFdToWorkflowStage('');
        setDeskList([]);
    };

    const handleSubmitFinalDecision = async () => {
        if (!finalDecisionTarget) return;
        if (!fdDecision) {
            showNotification('error', { title: 'Required', message: 'Please select a recommendation.' });
            return;
        }
        if (fdToWorkflowStage === '') {
            showNotification('error', { title: 'Required', message: 'Please select the next desk to move to.' });
            return;
        }
        setIsSubmittingFinalDecision(true);
        await new Promise(r => setTimeout(r, 0));
        try {
            const res = await journalAssessmentReportService.submitFinalDecision({
                submissionId: finalDecisionTarget.submissionId,
                decision: parseInt(fdDecision, 10),
                comment: fdComment,
                toWorkflowStage: fdToWorkflowStage as number,
            });
            if (res?.isSuccess === true) {
                showNotification('success', { title: 'Decision Submitted', message: 'The final journal assessment decision has been submitted successfully.' });
                closeFinalDecisionModal();
                fetchItems();
            } else {
                showNotification('error', { title: 'Submission Failed', message: res?.message || 'Failed to submit final decision.' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
        } finally {
            setIsSubmittingFinalDecision(false);
        }
    };

    // ── View Final Decision Drawer ────────────────────────────────────────────
    const openViewDecisionDrawer = async (row: JournalAssessmentProcessItem) => {
        setIsViewDrawerOpen(true);
        setViewDecisionData(null);
        setLoadingDecision(true);
        try {
            const res = await journalAssessmentReportService.getFinalDecisionBySubmissionId(row.submissionId);
            if (res?.isSuccess === true && res.data) {
                setViewDecisionData(res.data);
            } else {
                showNotification('error', { title: 'Error', message: res?.message || 'Failed to load final decision details' });
                setIsViewDrawerOpen(false);
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
            setIsViewDrawerOpen(false);
        } finally {
            setLoadingDecision(false);
        }
    };

    // ── Add Panel Modal ───────────────────────────────────────────────────────
    const openAddPanelModal = async (row: JournalAssessmentProcessItem) => {
        setAddPanelTarget(row);
        setSelectedAddMemberIds([]);
        setIsAddPanelOpen(true);
        setFetchingAddMembers(true);
        try {
            const response = await journalsByDeskService.getJournalAccessors();
            if (response.isSuccess && response.data) {
                const existingIds = new Set(row.assignments.map(a => a.memberId));
                setAddPanelMembers(response.data.filter(m => !existingIds.has(m.id)));
            } else {
                setAddPanelMembers([]);
                showNotification('error', { title: 'Error', message: response.message || 'Failed to load members' });
            }
        } catch {
            setAddPanelMembers([]);
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setFetchingAddMembers(false);
        }
    };

    const closeAddPanelModal = () => {
        setIsAddPanelOpen(false);
        setAddPanelTarget(null);
        setSelectedAddMemberIds([]);
        setAddPanelMembers([]);
    };

    const handleAddPanelMembers = async () => {
        if (!addPanelTarget || selectedAddMemberIds.length === 0) return;
        setIsAddingPanel(true);
        try {
            const assignedMembers = selectedAddMemberIds.map(id => {
                const member = addPanelMembers.find(m => m.id === id);
                return { memberId: id.toString(), name: member?.name ?? '' };
            });
            const response = await journalsByDeskService.assignMembersForJournalAssessment({
                submissionId: addPanelTarget.submissionId,
                assignedMembers,
            });
            if (response.isSuccess) {
                const results = response.data ?? [];
                const failed = results.filter(r => !r.successful);
                if (failed.length > 0) {
                    showNotification('error', {
                        title: 'Partial Assignment',
                        message: `${failed.length} member(s) could not be assigned: ${failed.map(f => f.reason).join(', ')}`,
                    });
                } else {
                    showNotification('success', { title: 'Success', message: 'Panel member(s) added successfully' });
                }
                closeAddPanelModal();
                fetchItems();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to add members' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setIsAddingPanel(false);
        }
    };

    // ── Delete Panel Modal ────────────────────────────────────────────────────
    const openDeletePanelModal = (row: JournalAssessmentProcessItem) => {
        setDeletePanelTarget(row);
        setSelectedDeleteAssignmentId('');
        setIsDeletePanelOpen(true);
    };

    const closeDeletePanelModal = () => {
        if (isDeletingMember) return;
        setIsDeletePanelOpen(false);
        setDeletePanelTarget(null);
        setSelectedDeleteAssignmentId('');
    };

    const handleDeletePanelMember = async () => {
        if (!selectedDeleteAssignmentId) {
            showNotification('error', { title: 'Required', message: 'Please select a member to remove.' });
            return;
        }
        setIsDeletingMember(true);
        try {
            const response = await journalAssessmentReportService.deleteJournalAssignment(selectedDeleteAssignmentId);
            if (response?.isSuccess) {
                showNotification('success', { title: 'Removed', message: 'Panel member removed successfully.' });
                closeDeletePanelModal();
                fetchItems();
            } else {
                showNotification('error', { title: 'Error', message: response?.message || 'Failed to remove member.' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
        } finally {
            setIsDeletingMember(false);
        }
    };

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getStatusChip = (status: string) => {
        const lower = status?.toLowerCase();
        if (lower === 'completed' || lower === 'submitted') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700">
                    <CheckCircle className="w-3 h-3" />{status}
                </span>
            );
        }
        if (lower === 'pending' || lower === 'not started' || lower === 'notstarted' || lower === 'in progress' || lower === 'inprogress') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700">
                    <Clock className="w-3 h-3" />{status}
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600">
                <XCircle className="w-3 h-3" />{status}
            </span>
        );
    };

    const formatFinalDecision = (decision: number | string | null) => {
        if (decision === 3 || decision === '3' || decision === 'AcceptableForFunding') return 'Acceptable for Funding';
        if (decision === 4 || decision === '4' || decision === 'NotAcceptableForFunding') return 'Not Acceptable for Funding';
        return decision ? String(decision) : '—';
    };

    const isPositiveDecision = (decision: number | string | null) =>
        decision === 3 || decision === '3' || decision === 'AcceptableForFunding';

    const handleViewAssessment = (assignmentId: string) => {
        navigate(`/admin/journal-assessment/${assignmentId}`);
    };

    const Spinner = () => (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    // ── Columns ───────────────────────────────────────────────────────────────
    const columns: TableColumn<JournalAssessmentProcessItem>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: (value) => <span className="text-gray-500 text-sm font-mono">{value}</span>,
        },
        {
            key: 'title',
            label: 'Title / Author',
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900 mb-0.5 leading-snug">{row.title}</div>
                    <div className="text-xs text-gray-500">
                        {row.fullName}
                        {row.journalRepresentativePosition && (
                            <span className="text-gray-400"> ({row.journalRepresentativePosition})</span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: 'journalBody',
            label: 'Journal Body',
            render: (value) => (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700">
                    {value || 'N/A'}
                </span>
            ),
        },
        {
            key: 'completedAssessment',
            label: 'Final Decision Made?',
            render: (value, row) => {
                if (!value) {
                    return (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-700">
                            <Clock className="w-3.5 h-3.5" />No
                        </span>
                    );
                }
                return (
                    <div className="space-y-1.5 flex flex-col">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold text-green-700">
                            <CheckCircle className="w-3.5 h-3.5" />Yes
                        </span>
                        {row.decision && (
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
                                row.decision === 'AcceptableForFunding' || row.decision === '3'
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : 'bg-red-50 text-red-700'
                            }`}>
                                {row.decision === 'AcceptableForFunding' || row.decision === '3'
                                    ? 'Acceptable for Funding'
                                    : row.decision === 'NotAcceptableForFunding' || row.decision === '4'
                                    ? 'Not Acceptable for Funding'
                                    : row.decision}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'assignments',
            label: 'Assessment Panel',
            render: (value, row) => {
                const assignments: JournalAssessmentAssignmentSummary[] = row.assignments ?? [];
                const allCompleted = assignments.length > 0 && assignments.every(a => a.status?.toLowerCase() === 'completed');
                const avgScore = allCompleted
                    ? (assignments.reduce((sum, a) => sum + (a.totalScore ?? 0), 0) / assignments.length).toFixed(1)
                    : null;

                if (assignments.length === 0) {
                    return <span className="text-xs text-gray-400 italic">No panel assigned</span>;
                }
                return (
                    <div className="space-y-1.5">
                        {assignments.map((a, idx) => (
                            <div key={a.journalAssignmentId} className="flex items-center gap-2 min-h-[28px]">
                                <div className="w-5 h-5 border rounded-full bg-gray-100 flex items-center justify-center text-gray-900 font-bold text-[10px] flex-shrink-0">
                                    {idx + 1}
                                </div>
                                <span className="text-xs text-gray-700 font-medium flex-1 min-w-0 truncate">
                                    {a.memberName}
                                </span>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    {getStatusChip(a.status)}
                                    {a.status?.toLowerCase() === 'completed' && (
                                        <span className="text-xs font-semibold text-gray-600 w-12 text-right">
                                            {a.totalScore?.toFixed(1)}%
                                        </span>
                                    )}
                                    <div className="w-6 flex justify-end">
                                        {a.status?.toLowerCase() === 'completed' && a.journalAssessmentId && (
                                            <button
                                                onClick={() => handleViewAssessment(a.journalAssignmentId)}
                                                title="View Assessment"
                                                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-[#088A61] transition-colors"
                                            >
                                                <Eye className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            key: 'assignments',
            label: 'Avg Score',
            render: (_, row) => {
                const assignments: JournalAssessmentAssignmentSummary[] = row.assignments ?? [];
                const allCompleted = assignments.length > 0 && assignments.every(a => a.status?.toLowerCase() === 'completed');
                if (!allCompleted) return <span className="text-xs text-gray-400 italic">Pending</span>;
                const avg = (assignments.reduce((sum, a) => sum + (a.totalScore ?? 0), 0) / assignments.length).toFixed(1);
                return (
                    <span className="text-sm font-bold text-[#088A61]">{avg}%</span>
                );
            },
        },
    ];

    // ── Actions ───────────────────────────────────────────────────────────────
    const actions: TableAction<JournalAssessmentProcessItem>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'View Submission Details',
                    onClick: (row) => {
                        setDetailsSubmissionId(row.submissionId);
                        setIsSubmissionDetailsOpen(true);
                    },
                    className: 'text-blue-600',
                },
                {
                    label: 'Make Final Decision',
                    onClick: (row) => openFinalDecisionModal(row),
                    className: (row) =>
                        !isJournalConvener || row.completedAssessment ? 'hidden' : 'text-blue-600',
                },
                {
                    label: 'View Final Decision',
                    onClick: (row) => openViewDecisionDrawer(row),
                    className: (row) => (!row.completedAssessment ? 'hidden' : 'text-blue-600'),
                },
                {
                    label: 'Add Panel Member',
                    onClick: (row) => openAddPanelModal(row),
                    className: (row) =>
                        row.completedAssessment ? 'hidden' : 'text-green-600',
                },
                {
                    label: 'Delete Panel Member',
                    onClick: (row) => openDeletePanelModal(row),
                    className: (row) => {
                        const hasRemovable = row.assignments.some(
                            a => a.status?.toLowerCase() === 'not started'
                        );
                        return row.completedAssessment || !hasRemovable ? 'hidden' : 'text-red-600';
                    },
                },
            ],
            title: 'Actions',
        },
    ];

    // ── Filter panel ──────────────────────────────────────────────────────────
    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Status</label>
                    {isJournalConvener ? (
                        <select
                            value={completedFilter ? 'true' : 'false'}
                            onChange={(e) => { setCompletedFilter(e.target.value === 'true'); setCurrentPage(1); }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="false">In Progress</option>
                            <option value="true">Completed</option>
                        </select>
                    ) : (
                        <select disabled className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                            <option value="true">Completed</option>
                        </select>
                    )}
                </div>
            </div>
            <button
                onClick={() => { setSearch(''); setCompletedFilter(isJournalConvener ? false : true); setCurrentPage(1); }}
                className="mt-4 text-sm text-blue-600 hover:underline"
            >
                Clear filters
            </button>
        </div>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="w-full">
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Journal Assessment Reports</h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    Track journal submissions currently under the assessment process.
                </p>
            </div>

            <ReusableTable
                title="Journal Assessment Process"
                subTitle="Journal submissions and their assessment panel assignments"
                columns={columns}
                data={items}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, author, or ID..."
                onSearch={(q) => { setSearch(q); setCurrentPage(1); }}
                emptyMessage="No journal assessments found."
                showSearch={true}
                showFilter={true}
                filterPanel={filterPanel}
                onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                isFilterOpen={isFilterOpen}
            />

            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}

            {/* ── Make Final Decision Modal ─────────────────────────────────── */}
            <Modal
                isOpen={isFinalDecisionOpen}
                onClose={closeFinalDecisionModal}
                title="Make Final Journal Assessment Decision"
                description="This decision is final and cannot be undone. Review carefully before submitting."
                maxWidth="lg"
            >
                <div className="space-y-3 mt-1">
                    {finalDecisionTarget && (
                        <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Journal</p>
                            <p className="text-sm font-semibold text-gray-900 line-clamp-1">{finalDecisionTarget.title}</p>
                            <p className="text-xs text-gray-400">{finalDecisionTarget.submissionNumber}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                                {finalDecisionTarget.fullName}
                                {finalDecisionTarget.journalRepresentativePosition && ` · ${finalDecisionTarget.journalRepresentativePosition}`}
                            </p>
                        </div>
                    )}

                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                        <p className="text-xs text-amber-700 font-medium">
                            This is <strong>irreversible</strong>. Ensure all panel assessments have been considered.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Recommendation <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={fdDecision}
                                onChange={e => setFdDecision(e.target.value as '' | '3' | '4')}
                                className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                            >
                                <option value="">Select…</option>
                                <option value="3">Acceptable for Funding</option>
                                <option value="4">Not Acceptable for Funding</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Move to Next Desk <span className="text-red-500">*</span>
                            </label>
                            {deskListLoading ? (
                                <div className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                    <Spinner />Loading…
                                </div>
                            ) : (
                                <select
                                    value={fdToWorkflowStage}
                                    onChange={e => setFdToWorkflowStage(e.target.value === '' ? '' : Number(e.target.value))}
                                    className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                                >
                                    <option value="">Select desk…</option>
                                    {deskList.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={fdComment}
                            onChange={e => setFdComment(e.target.value)}
                            placeholder="Add any notes about this decision…"
                            rows={2}
                            className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent resize-none"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                        <button
                            onClick={closeFinalDecisionModal}
                            disabled={isSubmittingFinalDecision}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmitFinalDecision}
                            disabled={isSubmittingFinalDecision}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSubmittingFinalDecision ? <><Spinner />Submitting…</> : 'Submit Final Decision'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── View Final Decision Drawer ────────────────────────────────── */}
            <Drawer
                isOpen={isViewDrawerOpen}
                onClose={() => { setIsViewDrawerOpen(false); setViewDecisionData(null); }}
                title="Final Journal Assessment Decision"
                width="max-w-2xl"
            >
                {loadingDecision ? (
                    <div className="flex items-center justify-center h-48 gap-3 text-gray-400">
                        <Spinner />
                        <span className="text-sm">Loading decision details…</span>
                    </div>
                ) : viewDecisionData ? (() => {
                    const d = viewDecisionData;
                    const formatDate = (s: string) => s
                        ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
                        : '—';
                    const isPositive = isPositiveDecision(d.decision);
                    const label = formatFinalDecision(d.decision);

                    return (
                        <div className="space-y-0 divide-y divide-gray-100">
                            <div className="pb-5">
                                <div className="flex flex-wrap items-center gap-2">
                                    {isPositive ? (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700">
                                            <CheckCircle className="w-3.5 h-3.5" />{label}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-red-50 text-red-700">
                                            <XCircle className="w-3.5 h-3.5" />{label}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="py-5 space-y-4">
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Final Decision Details</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className={`rounded-lg p-3 border ${isPositive ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-gray-500">Decision</p>
                                        <p className={`text-sm font-bold ${isPositive ? 'text-emerald-700' : 'text-red-700'}`}>{label}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-gray-500">Decision Date</p>
                                        <p className="text-sm font-medium text-gray-800">{formatDate(d.dateCreated)}</p>
                                    </div>
                                </div>
                                {d.comment && (
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Comment</p>
                                        <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                                            {d.comment}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="py-5">
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Decision Made By</h3>
                                {d.decidedBy?.person ? (
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm flex-shrink-0">
                                            {d.decidedBy.person.firstName?.[0]}{d.decidedBy.person.lastName?.[0]}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-gray-900 text-sm">{d.decidedBy.person.fullName}</p>
                                            <p className="text-xs text-gray-500">{d.decidedBy.person.email}</p>
                                            {d.decidedBy.person.institution?.name && (
                                                <p className="text-xs text-gray-400 mt-0.5">{d.decidedBy.person.institution.name}</p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">Decider information unavailable</p>
                                )}
                            </div>
                        </div>
                    );
                })() : null}
            </Drawer>

            {/* ── Add Panel Member Modal ────────────────────────────────────── */}
            <Modal
                isOpen={isAddPanelOpen}
                onClose={closeAddPanelModal}
                title="Add Panel Member"
                description="Select one or more journal accessors to add to the assessment panel."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {addPanelTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Journal</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{addPanelTarget.title}</p>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Members to Add <span className="text-red-500">*</span>
                        </label>
                        {fetchingAddMembers ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <Spinner />Loading members…
                            </div>
                        ) : addPanelMembers.length === 0 ? (
                            <div className="w-full px-3 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                                No additional members available
                            </div>
                        ) : (
                            <MultiSelect
                                options={addPanelMembers.map(m => ({ value: m.id, label: m.name }))}
                                selectedValues={selectedAddMemberIds}
                                onChange={setSelectedAddMemberIds}
                                placeholder="Select members to add..."
                            />
                        )}
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeAddPanelModal} disabled={isAddingPanel}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleAddPanelMembers} disabled={isAddingPanel || selectedAddMemberIds.length === 0}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isAddingPanel ? <><Spinner />Adding…</> : 'Add Member(s)'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Delete Panel Member Modal ─────────────────────────────────── */}
            <Modal
                isOpen={isDeletePanelOpen}
                onClose={closeDeletePanelModal}
                title="Remove Panel Member"
                description="Only members who have not yet started their assignment can be removed."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {deletePanelTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Journal</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{deletePanelTarget.title}</p>
                        </div>
                    )}
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                        <p className="text-xs text-red-700 font-medium">This will permanently remove the member from this assessment panel.</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Select Member to Remove <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedDeleteAssignmentId}
                            onChange={e => setSelectedDeleteAssignmentId(e.target.value)}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        >
                            <option value="">Select member…</option>
                            {deletePanelTarget?.assignments
                                .filter(a => a.status?.toLowerCase() === 'not started')
                                .map(a => (
                                    <option key={a.journalAssignmentId} value={a.journalAssignmentId}>
                                        {a.memberName}
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeDeletePanelModal} disabled={isDeletingMember}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleDeletePanelMember} disabled={isDeletingMember || !selectedDeleteAssignmentId}
                            className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isDeletingMember ? <><Spinner />Removing…</> : 'Remove Member'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Submission Details Drawer ─────────────────────────────────── */}
            <Drawer
                isOpen={isSubmissionDetailsOpen}
                onClose={() => { setIsSubmissionDetailsOpen(false); setDetailsSubmissionId(null); }}
                title="Journal Details"
                width="max-w-3xl"
            >
                {detailsSubmissionId && (
                    <AdminSubmissionDetails submissionId={detailsSubmissionId} />
                )}
            </Drawer>
        </div>
    );
}

export default JournalAssessmentReports;